import {Card} from "@/components/card/Card.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import {ScrollableUptimeBar} from "@/pages/statuspages/consumption/components/ScrollableUptimeBar/ScrollableUptimeBar.tsx";
import {BarEventStatus} from "@uptime-com/uptimebar";
import {useMemo} from "react";

import type {ComponentDrilldownResult} from "../../types.ts";
import {generateEventsFromDowntimes} from "../../utils/uptimeBarEvents.ts";
import styles from "./DrilldownUptimeBar.module.scss";

interface DrilldownUptimeBarProps {
  drilldown: ComponentDrilldownResult;
  start: Date;
  end: Date;
  timezone: string;
}

export const DrilldownUptimeBar = ({drilldown, start, end, timezone}: DrilldownUptimeBarProps) => {
  // Legacy parity: the check-analysis statechart is driven purely by alerts, so time without a
  // recorded outage is up. Deriving the bar from data coverage instead disagreed with the uptime
  // figure beside it — that is computed from the same alerts, so a check with no outages read as
  // 100% uptime next to a fully grey bar.
  //
  // Legacy also stops its series at the current time. Selecting a range that includes today ends
  // the window at 23:59:59, and the check cannot have reported that yet — rendering it left a
  // stretch of operational filler trailing the bar on every selection.
  const renderedEnd = useMemo(() => {
    const now = new Date();
    return end.getTime() > now.getTime() ? now : end;
  }, [end]);

  const events = useMemo(
    () => generateEventsFromDowntimes(drilldown.downtime_history ?? [], start, renderedEnd),
    [drilldown.downtime_history, start, renderedEnd]
  );

  return (
    <Card variant="white" className={styles.uptimeSection}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Uptime</span>
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <Dot variant="green" size="legend" />
            Uptime
          </span>
          <span className={styles.legendItem}>
            <Dot variant="red" size="legend" />
            Downtime
          </span>
          <span className={styles.legendItem}>
            <Dot variant="yellow" size="legend" />
            Maintenance
          </span>
          <span className={styles.legendItem}>
            <Dot variant="gray" size="legend" />
            No Data
          </span>
        </div>
      </div>
      <ScrollableUptimeBar
        start={start}
        end={renderedEnd}
        timezone={timezone}
        events={events}
        size="large"
        defaultMissingEventStatus={BarEventStatus.OPERATIONAL}
        stripedNoData
        enableMobileScroll
      />
    </Card>
  );
};
